<template>
  <div class="orders-list">
    <div class="page-header">
      <div>
        <h1>Мои заказы</h1>
        <p class="page-subtitle">История ваших заказов и быстрый переход к деталям</p>
      </div>

      <div v-if="orders.length > 0" class="stats">
        <span>Всего заказов: {{ totalOrdersCount }}</span>
        <span>Общая сумма: {{ formatPrice(totalSpent) }}</span>
      </div>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Загрузка заказов...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <Button
        label="Повторить"
        icon="pi pi-refresh"
        class="retry-btn"
        @click="loadOrders"
      />
    </div>

    <div v-else-if="orders.length === 0" class="empty-state">
      <i class="pi pi-inbox" style="font-size: 48px; opacity: 0.5"></i>
      <p>У вас пока нет заказов</p>
      <router-link to="/products" custom v-slot="{ navigate }">
        <Button
          label="Перейти в каталог"
          class="catalog-btn"
          @click="navigate"
        />
      </router-link>
    </div>

    <div v-else class="orders-grid">
      <div
        v-for="order in sortedOrders"
        :key="order.id"
        class="order-card"
        @click="goToOrder(order.id)"
      >
        <div class="order-header">
          <span class="order-number">Заказ #{{ order.id }}</span>
          <span class="order-date">{{ formatDate(order.createdAt) }}</span>
        </div>

        <span class="status-chip" :class="getStatusClass(order.status)">
          {{ getOrderStatus(order) }}
        </span>

        <div class="order-body">
          <div class="order-info">
            <span class="label">Сумма:</span>
            <strong class="amount">{{ formatPrice(order.totalAmount) }}</strong>
          </div>

          <div class="order-info">
            <span class="label">Товаров:</span>
            <span>{{ order.items?.length || 0 }} шт.</span>
          </div>
        </div>

        <div class="order-footer">
          <Button
            label="Подробнее"
            icon="pi pi-arrow-right"
            iconPos="right"
            class="details-btn"
            text
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import { useAppToast } from '@/shared/lib/useAppToast'
import { usePermissions } from '@/shared/composables/usePermissions'
import { useAuthStore } from '@/modules/auth/store/auth.store'
import { ORDER_STATUSES } from '../api/orders.service'
import { useOrdersStore } from '../store/orders.store'

const router = useRouter()
const toast = useAppToast()
const authStore = useAuthStore()
const ordersStore = useOrdersStore()
const { can } = usePermissions()

const {
  orders,
  isLoading,
  error,
  totalOrdersCount,
  totalSpent,
  sortedOrders
} = storeToRefs(ordersStore)

const canViewAllOrders = () => {
  const roles = authStore.user?.roles ?? []
  return (
    roles.includes('Manager') ||
    roles.includes('Admin') ||
    roles.includes('God') ||
    can('ChangeOrderStatus')
  )
}

const loadOrders = async () => {
  if (canViewAllOrders()) {
    await router.replace('/admin/orders')
    return
  }

  try {
    await ordersStore.fetchMyOrders()
  } catch (e) {
    toast.error(e?.message || 'Не удалось загрузить заказы')
  }
}

const goToOrder = (orderId) => {
  router.push(`/orders/${orderId}`)
}

const getOrderStatus = (order) => {
  return order?.status || ORDER_STATUSES[0]
}

const getStatusClass = (status) => {
  const normalizedStatus = status || ORDER_STATUSES[0]

  return {
    'status-new': normalizedStatus === 'Новая',
    'status-progress': normalizedStatus === 'В работе',
    'status-delivery': normalizedStatus === 'Доставка',
    'status-paid': normalizedStatus === 'Оплачено/Закрыта',
    'status-unpaid': normalizedStatus === 'Неоплачена/Закрыта'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '—'

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) {
    return dateString
  }

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price || 0)
}

watch(
  () => error.value,
  (newError) => {
    if (newError) {
      toast.error(newError)
    }
  }
)

onMounted(() => {
  loadOrders()
})
</script>

<style scoped>
.orders-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
  color: #1d1d1d;
}

.page-subtitle {
  margin: 8px 0 0;
  color: #6c757d;
}

.stats {
  display: flex;
  gap: 20px;
  padding: 10px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  font-size: 14px;
}

.stats span:first-child {
  border-right: 1px solid #dee2e6;
  padding-right: 20px;
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.order-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.order-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
  border-color: #c41217;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.order-number {
  font-weight: 700;
  font-size: 18px;
  color: #c41217;
}

.order-date {
  font-size: 12px;
  color: #6c757d;
}

.order-body {
  margin-top: 12px;
  margin-bottom: 16px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
}

.status-new {
  background: #eef2ff;
  color: #3730a3;
}

.status-progress {
  background: #fff7ed;
  color: #9a3412;
}

.status-delivery {
  background: #ecfeff;
  color: #0e7490;
}

.status-paid {
  background: #ecfdf5;
  color: #047857;
}

.status-unpaid {
  background: #fef2f2;
  color: #b91c1c;
}

.order-info {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}

.label {
  color: #6c757d;
  font-size: 14px;
}

.amount {
  font-size: 18px;
  color: #c41217;
}

:deep(.details-btn.p-button) {
  width: 100%;
  padding: 10px;
  background: none;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  color: #6c757d;
  box-shadow: none;
  font-weight: 500;
}

:deep(.details-btn.p-button:hover) {
  background: #f8f9fa;
  border-color: #c41217;
  color: #c41217;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #c41217;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.error-message {
  color: #dc3545;
  margin-bottom: 20px;
}

:deep(.retry-btn.p-button),
:deep(.catalog-btn.p-button) {
  padding: 12px 24px;
  background: #c41217;
  border: none;
  color: white;
  border-radius: 10px;
  box-shadow: none;
  font-weight: 600;
}

:deep(.retry-btn.p-button:hover),
:deep(.catalog-btn.p-button:hover) {
  background: #a00e12;
}

.empty-state i {
  margin-bottom: 16px;
  color: #adb5bd;
}

.empty-state p {
  margin-bottom: 20px;
  color: #6c757d;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .orders-list {
    padding: 16px 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .stats {
    flex-direction: column;
    gap: 8px;
    padding: 10px 14px;
    width: 100%;
  }

  .stats span:first-child {
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 8px;
  }

  .orders-grid {
    grid-template-columns: 1fr;
  }
}
</style>
